import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Shield, ChevronRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Static departments data - will always work regardless of database
const STATIC_DEPARTMENTS = [
  { id: "1", name: "Paris", code: "75", slug: "paris-75" },
  { id: "2", name: "Hauts-de-Seine", code: "92", slug: "hauts-de-seine-92" },
  { id: "3", name: "Seine-Saint-Denis", code: "93", slug: "seine-saint-denis-93" },
  { id: "4", name: "Val-de-Marne", code: "94", slug: "val-de-marne-94" },
  { id: "5", name: "Yvelines", code: "78", slug: "yvelines-78" },
  { id: "6", name: "Val-d'Oise", code: "95", slug: "val-doise-95" },
  { id: "7", name: "Essonne", code: "91", slug: "essonne-91" },
  { id: "8", name: "Seine-et-Marne", code: "77", slug: "seine-et-marne-77" }
];

export default function Footer() {
  const [config, setConfig] = useState(null);
  const [departments, setDepartments] = useState(STATIC_DEPARTMENTS);
  const [services, setServices] = useState([]);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/config`).then(res => res.json()),
      fetch(`${API_URL}/api/departments`).then(res => res.json()).catch(() => []),
      fetch(`${API_URL}/api/services`).then(res => res.json()).catch(() => [])
    ])
      .then(([configData, departmentsData, servicesData]) => {
        setConfig(configData);
        // Use API data if available, otherwise keep static data
        if (departmentsData && departmentsData.length > 0) {
          setDepartments(departmentsData);
        }
        setServices(servicesData.slice(0, 5));
      })
      .catch(console.error);
  }, []);

  return (
    <footer className="bg-[#0a1628] text-white" data-testid="footer">
      {/* CTA Banner */}
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 py-8">
        <div className="container-custom text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
            Besoin d'un serrurier Fichet ?
          </h3>
          <p className="text-white/90 mb-4">
            Intervention rapide 7j/7 en Île-de-France
          </p>
          {config && (
            <a
              href={config.phone_link}
              onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
              className="inline-flex items-center gap-2 bg-white text-[#0f2744] px-8 py-4 rounded-lg font-bold text-xl hover:bg-gray-100 transition-colors"
              data-testid="footer-cta-phone"
            >
              <Phone className="w-6 h-6" />
              {config.phone}
            </a>
          )}
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-12 md:py-16">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-amber-400" />
                </div>
                <span className="text-xl font-bold">Fichet Dépannage</span>
              </div>
              <p className="text-gray-400 mb-4 text-sm">
                Entreprise indépendante spécialisée dans le dépannage de serrures Fichet. 
                Nous ne faisons pas partie du réseau officiel Point Fort Fichet.
              </p>
              <div className="space-y-2 text-sm">
                {config && (
                  <>
                    <a 
                      href={config.phone_link}
                      onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
                      className="flex items-center gap-2 text-gray-300 hover:text-amber-400 transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      {config.phone}
                    </a>
                    <a 
                      href={`mailto:${config.email}`}
                      className="flex items-center gap-2 text-gray-300 hover:text-amber-400 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      {config.email}
                    </a>
                  </>
                )}
                <div className="flex items-center gap-2 text-gray-300">
                  <Clock className="w-4 h-4" />
                  7j/7 - 24h/24
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <MapPin className="w-4 h-4" />
                  Paris et Île-de-France
                </div>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Nos Services</h4>
              <ul className="space-y-2">
                {services.map((service) => (
                  <li key={service.id}>
                    <Link
                      to={`/services/${service.slug}`}
                      className="flex items-center gap-1 text-gray-400 hover:text-amber-400 transition-colors text-sm"
                    >
                      <ChevronRight className="w-3 h-3" />
                      {service.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/services"
                    className="flex items-center gap-1 text-amber-400 hover:text-amber-300 transition-colors text-sm font-medium"
                  >
                    <ChevronRight className="w-3 h-3" />
                    Tous les services
                  </Link>
                </li>
              </ul>
            </div>

            {/* Zones - Départements */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Zones d'intervention</h4>
              <ul className="space-y-2">
                {departments.map((dept) => (
                  <li key={dept.id}>
                    <Link
                      to={`/departement/${dept.slug}`}
                      className="flex items-center gap-1 text-gray-400 hover:text-amber-400 transition-colors text-sm"
                    >
                      <ChevronRight className="w-3 h-3" />
                      {dept.name} ({dept.code})
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Informations</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/faq"
                    className="flex items-center gap-1 text-gray-400 hover:text-amber-400 transition-colors text-sm"
                  >
                    <ChevronRight className="w-3 h-3" />
                    Questions fréquentes
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="flex items-center gap-1 text-gray-400 hover:text-amber-400 transition-colors text-sm"
                  >
                    <ChevronRight className="w-3 h-3" />
                    Contact
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services"
                    className="flex items-center gap-1 text-gray-400 hover:text-amber-400 transition-colors text-sm"
                  >
                    <ChevronRight className="w-3 h-3" />
                    Tarifs indicatifs
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 py-6">
        <div className="container-custom flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
          <p>© {new Date().getFullYear()} Fichet Dépannage. Tous droits réservés.</p>
          <p className="text-center md:text-right text-xs">
            Entreprise indépendante - Non affiliée au réseau Point Fort Fichet
          </p>
        </div>
      </div>

      {/* Schema.org LocalBusiness structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Fichet Dépannage",
          "description": "Dépannage de serrures Fichet 7j/7 - 24h/24 en Île-de-France. Ouverture de porte, réparation, remplacement de cylindre.",
          "telephone": config?.phone || "09 72 57 44 50",
          "email": config?.email || "contact@fichet-depannage.fr",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Paris",
            "addressRegion": "Île-de-France",
            "addressCountry": "FR"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "48.8566",
            "longitude": "2.3522"
          },
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "00:00",
            "closes": "23:59"
          },
          "priceRange": "€€",
          "areaServed": ["Paris", "Boulogne-Billancourt", "Vincennes", "Levallois-Perret", "Neuilly-sur-Seine"]
        })
      }} />
    </footer>
  );
}
